import {
  Appointment,
  Cancelable,
  DXS,
  SchedulerRef,
  useDxScheduler,
} from '@lmt/unicorn-shared/app/common/devexpress/Scheduler/dx-scheduler';
import { IntervalSelection } from '@lmt/unicorn-shared/app/components/calendar/assistant/IntervalSelection';
import { PortalToTable } from '@lmt/unicorn-shared/app/components/calendar/assistant/PortalToTable';
import { ResourcePanel } from '@lmt/unicorn-shared/app/components/calendar/assistant/ResourcePanel';
import { dxSchedulerWorkspace } from '@lmt/unicorn-shared/app/components/calendar/devextreme/dxSchedulerWorkspace';
import { localFormat } from '@lmt/unicorn-shared/app/helpers/date-helper';
import clsx from 'clsx';
import {
  addDays,
  addMilliseconds,
  differenceInMilliseconds,
  getTime,
  Interval,
  isSameDay,
  isWithinInterval,
  startOfWeek,
  subHours,
  toDate,
} from 'date-fns';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useFirstDayOfWeek } from '../common';
import {
  patientResourceType,
  ResourceSettings,
  SelectedResources,
  TakenTimeSlotsForResources,
} from '../visit/common/types';
import styles from './SchedulingAssistant.module.scss';
import { sortResourceKeys } from './resources-helper';
import {
  SchedulerResourceGroups,
  SchedulerResourceToPick,
} from './scheduling-assistant-types';
import { useEventCallback } from '@mui/material';
import { isSelectedIntervalAlreadyTaken } from '../visit/common/time-operations';

const resourceId = 'resourceId';
const groups = [resourceId];
const cellHeight = 40;
const dayViewCellDurationInMinutes = 60;
const weekViewCellDurationInMinutes = 1440; // 24 hours
const defaultDayViewCellWidth = 50;
const defaultWeekViewCellWidth = 105;

interface SchedulingAssistantProps {
  rootClassName?: string;
  selectedInterval?: Interval;
  resourceSettings: ResourceSettings;
  resourceGroups: SchedulerResourceGroups;
  onIntervalChanged: (newInterval: Interval) => void;
  selectedResources: SelectedResources;
  setResourceSelected: (
    type: string,
    resourceId: number,
    selected: boolean,
  ) => void;
  takenTimeSlots: Interval[];
  takenTimeSlotsForResources: TakenTimeSlotsForResources;
  isWeekView: boolean;
  stepsInCell?: number;
  typesWithAnyAvailableResource: string[];
  anyAvailableResourceEnabled?: boolean; // if true, there is a special option 'Any available' in resource list
  setTypesWithAnyAvailableResource?: (newTypes: string[]) => void;
  unavailableTimeSlots: Record<string, Interval[]>; //  key is resource type, value is an array of intervals where all resources of given type are occupied
  resourcesLoaded?: boolean;
}

interface SchedulerResource {
  id: number | string;
  text: string;
  type: 'normal' | 'group';
}

function makeResourceId(type: string, resourceId: number) {
  return `${type}_${resourceId}`;
}

export const SchedulingAssistant: FC<SchedulingAssistantProps> = React.memo(
  function SchedulingAssistant(props) {
    const {
      rootClassName,
      selectedInterval,
      resourceSettings,
      resourceGroups,
      onIntervalChanged,
      takenTimeSlots,
      takenTimeSlotsForResources,
      setResourceSelected,
      selectedResources,
      isWeekView,
      stepsInCell,
      anyAvailableResourceEnabled,
      typesWithAnyAvailableResource,
      setTypesWithAnyAvailableResource,
      unavailableTimeSlots,
      resourcesLoaded,
    } = props;
    useDxScheduler();
    const selectedResourcesRef = useRef(selectedResources);
    selectedResourcesRef.current = selectedResources;
    const firstDayOfWeekIndex = useFirstDayOfWeek();

    const cellDurationInMinutes = isWeekView
      ? weekViewCellDurationInMinutes
      : dayViewCellDurationInMinutes; // 24 hours or 1 hour

    const schedulerRef = useRef<SchedulerRef>(null);
    const getSchedulerWorkspace = useCallback(
      () =>
        (schedulerRef.current?.instance() as any)?._workSpace as
          | dxSchedulerWorkspace
          | undefined,
      [],
    );

    const startOfShownDatePeriod = useMemo(() => {
      const options = {
        weekStartsOn: firstDayOfWeekIndex,
      };
      return startOfWeek(
        selectedInterval ? selectedInterval.start : new Date(),
        options,
      );
    }, [selectedInterval]);

    const calculatedCellWidth = getSchedulerWorkspace()?.getCellWidth();

    const schedulerParameters = useMemo(() => {
      const weekStart = startOfShownDatePeriod;
      return {
        cellWidth:
          // scheduler workspace may return incorrect value for cell width when scheduler is being re-rendered
          // we additionally check the value, it can't be less than 50px
          // prevents bug #34571
          calculatedCellWidth && calculatedCellWidth >= defaultDayViewCellWidth
            ? calculatedCellWidth
            : isWeekView
              ? defaultWeekViewCellWidth
              : defaultDayViewCellWidth,
        start: weekStart,
        // if we use `endOfWeek` here, then end date would be 23:59
        // (while it should be possible to select 00:00 of next week)
        end: addDays(weekStart, 7),
        cellDurationInMilliseconds: cellDurationInMinutes * 60 * 1000,
      };
    }, [
      isWeekView,
      startOfShownDatePeriod,
      cellDurationInMinutes,
      calculatedCellWidth,
      firstDayOfWeekIndex,
    ]);

    const offsetDetector = useEventCallback((dateTime: Date | number) => {
      const start = schedulerParameters.start;
      const end = schedulerParameters.end;
      if (getTime(dateTime) <= getTime(start)) {
        return 0;
      } else if (getTime(dateTime) >= getTime(end)) {
        dateTime = end;
      }
      const cellWidth = schedulerParameters.cellWidth;
      const cellDuration = schedulerParameters.cellDurationInMilliseconds;

      const result =
        (differenceInMilliseconds(dateTime, start) / cellDuration) * cellWidth;
      return result;
    });

    const timeDetector = useEventCallback((offset: number) => {
      const cellWidth = schedulerParameters.cellWidth;
      const cellDuration = schedulerParameters.cellDurationInMilliseconds;

      const startViewDate = schedulerParameters.start;
      return addMilliseconds(
        startViewDate,
        (offset / cellWidth) * cellDuration,
      );
    });

    const onSelectedIntervalChanged = useEventCallback(
      (newInterval: Interval) => {
        onIntervalChanged(newInterval);
      },
    );

    const [schedulerReady, setSchedulerReady] = useState(false);
    const scrollToInterval = useEventCallback((interval?: Interval) => {
      if (!interval) return;
      const offset = offsetDetector(subHours(toDate(interval.start), 1));
      getSchedulerWorkspace()?.getScrollable().scrollTo({
        left: offset,
        top: 0,
      });
    });

    const selectAnyAvailableResource = useEventCallback((type: string) => {
      if (!selectedInterval || !resourcesLoaded) {
        return;
      }

      const resourceGroup = resourceGroups[type];
      if (!resourceGroup) {
        return;
      }
      const takenTimeSlots = takenTimeSlotsForResources[type] ?? [];

      for (const resource of resourceGroup) {
        const resourceTimeSlots = takenTimeSlots.find(
          (x) => x.resourceId === resource.id,
        );
        if (
          !resourceTimeSlots ||
          !isSelectedIntervalAlreadyTaken(
            selectedInterval,
            resourceTimeSlots.slots,
          )
        ) {
          const selectedResourcesOfType = selectedResources[type] ?? [];
          if (!selectedResourcesOfType.some((x) => x.id === resource.id)) {
            setResourceSelected(type, resource.id, true);
          }
          if (resourceSettings[type].isMultiSelect) {
            for (const item of selectedResourcesOfType) {
              if (item.id !== resource.id) {
                removeResource(type, item.id);
              }
            }
          }
          return;
        }
      }
    });

    useEffect(
      function selectAnyAvailableResourceIfNecessary() {
        if (!anyAvailableResourceEnabled || !resourcesLoaded) {
          return;
        }
        for (const type of typesWithAnyAvailableResource) {
          selectAnyAvailableResource(type);
        }
      },
      [selectedInterval, typesWithAnyAvailableResource, resourcesLoaded],
    );

    const flatUnavailableTimeSlots = useMemo(() => {
      return typesWithAnyAvailableResource.flatMap(
        (type) => unavailableTimeSlots[type] ?? [],
      );
    }, [typesWithAnyAvailableResource, unavailableTimeSlots]);

    useEffect(() => {
      scrollToInterval(selectedInterval);
    }, [selectedInterval]);

    const onSchedulerContentReady = useEventCallback(() => {
      setSchedulerReady(true);
      scrollToInterval(selectedInterval);
    });

    // we store shown resources in a Ref (instead of State), because we need to sync it with Props.selectedResources,
    // but also have an ability to modify it internally.

    const shownResourcesRef = useRef<SchedulerResourceGroups>({});

    const setShownResources = useCallback((value: SchedulerResourceGroups) => {
      shownResourcesRef.current = { ...value };
      rerender((i) => i + 1);
    }, []);
    const [, rerender] = useState(0);
    // we do it in useMemo, because if done in useEffect it will call another re-rendering and it will cause
    // Bug 28218: Day panel disappears
    useMemo(
      function addSelectedResourcesToShownResourcesIfNotExist() {
        let isChange = false;
        Object.keys(props.resourceSettings).forEach((type) => {
          if (!shownResourcesRef.current[type]) {
            isChange = true;
            shownResourcesRef.current[type] = [];
          }
        });
        Object.keys(props.selectedResources).forEach((type) => {
          const selectedResourcesOfGivenType = props.selectedResources[type];
          selectedResourcesOfGivenType.forEach((selectedResource) => {
            const group = shownResourcesRef.current[type];
            if (!group) return;
            if (!group.find((x) => x.id === selectedResource.id)) {
              isChange = true;
              if (type === patientResourceType) {
                shownResourcesRef.current[type] = [];
              }
              shownResourcesRef.current[type].push(selectedResource);
            }
          });
        });
        if (isChange) {
          shownResourcesRef.current = { ...shownResourcesRef.current };
        }
      },
      [props.selectedResources, props.resourceSettings],
    );

    const pickResource = useEventCallback(
      (resourceType: string, resource: SchedulerResourceToPick) => {
        if (resourceType) {
          const shownResources = { ...shownResourcesRef.current };

          const shownResourcesForType = shownResources[resourceType] || [];
          const hasMultiSelect = resourceSettings[resourceType]?.isMultiSelect;
          if (
            (shownResourcesForType.length === 0 && !hasMultiSelect) ||
            hasMultiSelect
          ) {
            setResourceSelected(resourceType, resource.id, true);
          }
          shownResources[resourceType] = [
            ...(shownResourcesForType || []),
            resource,
          ];
          setShownResources(shownResources);
        }
      },
    );

    const pickAllResources = useEventCallback((resourceType: string) => {
      const allResourcesOfType = resourceGroups[resourceType]?.filter(
        (x) => !x.isDeleted,
      );
      if (!allResourcesOfType?.length) {
        return;
      }
      const selectedResourcesOfType = selectedResources[resourceType]?.map(
        (x) => x.id,
      );

      const shownResources = { ...shownResourcesRef.current };
      shownResources[resourceType] = [...allResourcesOfType];
      setShownResources(shownResources);

      const hasMultiSelect = resourceSettings[resourceType]?.isMultiSelect;
      if (hasMultiSelect) {
        (selectedResourcesOfType?.length
          ? allResourcesOfType.filter(
              (resource) => !selectedResourcesOfType.includes(resource.id),
            )
          : allResourcesOfType
        ).forEach((resource) =>
          setResourceSelected(resourceType, resource.id, true),
        );
      } else if (!selectedResourcesOfType?.length) {
        setResourceSelected(resourceType, allResourcesOfType[0].id, true);
      }
    });

    const addTypeWithAnyAvailableResource = useEventCallback((type: string) => {
      setTypesWithAnyAvailableResource?.([
        ...typesWithAnyAvailableResource,
        type,
      ]);
    });

    const removeTypeWithAnyAvailableResource = useEventCallback(
      (type: string) => {
        setTypesWithAnyAvailableResource?.(
          typesWithAnyAvailableResource.filter((x) => x !== type),
        );
      },
    );

    const removeResource = useEventCallback(
      (resourceType: string, resourceId: number) => {
        setResourceSelected(resourceType, resourceId, false);

        const newShownResources = { ...shownResourcesRef.current };
        newShownResources[resourceType] = newShownResources[
          resourceType
        ].filter((shownResource) => shownResource.id !== resourceId);

        setShownResources(newShownResources);
      },
    );

    const removeAllResources = useEventCallback((resourceType: string) => {
      const shownResources = { ...shownResourcesRef.current };
      shownResources[resourceType] = [];
      setShownResources(shownResources);

      const selectedResourcesOfGivenType = selectedResources[resourceType];
      selectedResourcesOfGivenType?.forEach((resource) =>
        setResourceSelected(resourceType, resource.id, false),
      );
    });

    const resourcesForScheduler = useMemo(
      function convertResourcesToDxSchedulerFormat() {
        const result: Array<SchedulerResource> = [];
        sortResourceKeys(resourceGroups).forEach((type) => {
          if (!resourceSettings[type]?.isEmbedded) {
            result.push({ id: type, text: type, type: 'group' });
          }
          const shownResourcesForType = shownResourcesRef.current[type] || [];
          shownResourcesForType.forEach((typeResource) =>
            result.push({
              id: makeResourceId(type, typeResource.id),
              text: typeResource.name,
              type: 'normal',
            }),
          );
        });
        return result;
      },
      [shownResourcesRef.current, resourceGroups],
    );

    const appointmentsData: Appointment[] = useMemo(() => {
      const result: Appointment[] = [];
      Object.keys(takenTimeSlotsForResources).forEach((type) => {
        for (const timeSlot of unavailableTimeSlots[type]) {
          result.push({
            resourceId: type,
            startDate: toDate(timeSlot.start),
            endDate: toDate(timeSlot.end),
          });
        }
        takenTimeSlotsForResources[type]
          .flatMap((resourcesTimeSlots: any) => resourcesTimeSlots)
          .forEach(
            (resourceTimeSlots: { slots: any[]; resourceId: number }) => {
              const timeSlotsAsAppointments: Appointment[] =
                resourceTimeSlots.slots.map(
                  (timeSlot: { start: number | Date; end: number | Date }) => ({
                    startDate: toDate(timeSlot.start),
                    endDate: toDate(timeSlot.end),
                    resourceId: makeResourceId(
                      type,
                      resourceTimeSlots.resourceId,
                    ),
                  }),
                );
              result.push(...timeSlotsAsAppointments);
            },
          );
      });
      return result;
    }, [takenTimeSlotsForResources, unavailableTimeSlots]);

    const dateCellRender = useEventCallback(
      (args: { text: string; date: Date }) => {
        const isCurrentDateWithinInterval =
          selectedInterval &&
          (isSameDay(args.date, selectedInterval.start) ||
            isSameDay(args.date, selectedInterval.end) ||
            isWithinInterval(args.date, selectedInterval)) &&
          args.date.getTime() !== new Date(selectedInterval.end).getTime();

        return (
          <div
            className={clsx(
              styles.dateHeader,
              isWeekView && styles.dateHeaderWeekView,
              isCurrentDateWithinInterval &&
                (isWeekView
                  ? styles.dateHeaderSelectedWeekView
                  : styles.dateHeaderSelected),
            )}
          >
            {isWeekView
              ? args.text.toUpperCase()
              : localFormat(args.date, 'PPPP')}
          </div>
        );
      },
    );

    const appointmentComponent = useEventCallback(() => {
      return <div className={styles.schedulerAppointment} />;
    });

    //if the key includes selectedInterval we get a bug: Bug 68645: Visit/absence time interval isn't displayed in scheduler assistant
    const schedulerViewKey = isWeekView.toString();

    const selectionViewKey = useMemo(() => {
      return `selection_${new Date().getTime()}`;
    }, [schedulerViewKey, resourcesForScheduler]);

    // we need to add light-green background to rows with selected resources
    // overriding `dataCellRender` is too expensive for that
    const customCss = useMemo(() => {
      const selectedResourcesDict: Record<string, boolean> = {};
      Object.keys(selectedResources).forEach((key) => {
        selectedResources[key].forEach(
          (resource) => (selectedResourcesDict[`${key}_${resource.id}`] = true),
        );
      });
      return resourcesForScheduler
        .map((resource, index) => {
          if (selectedResourcesDict[resource.id])
            return `.scheduler-selected-resources .dx-scheduler-work-space table.dx-scheduler-date-table tbody tr:nth-child(${
              index + 1
            }) {
        background-color: rgba(57, 189, 188, 0.1); //$gridRowSelection;
    }`;
          else return '';
        })
        .join('');
    }, [selectedResources]);

    const takenTimeSlotsWithUnavailable = useMemo(
      () => [...takenTimeSlots, ...flatUnavailableTimeSlots],
      [takenTimeSlots, flatUnavailableTimeSlots],
    );
    return (
      <div
        className={clsx(
          styles.schedulerAssistantRoot,
          rootClassName,
          isWeekView && styles.nightView,
        )}
      >
        <style>{customCss}</style>
        <ResourcePanel
          shownResources={shownResourcesRef.current}
          pickResource={pickResource}
          pickAllResources={pickAllResources}
          removeAllResources={removeAllResources}
          removeResource={removeResource}
          resourceGroups={resourceGroups}
          resourceSettings={resourceSettings}
          selectedResources={selectedResources}
          setResourceSelected={setResourceSelected}
          anyAvailableResourceEnabled={anyAvailableResourceEnabled}
          typesWithAnyAvailableResource={typesWithAnyAvailableResource}
          addTypeWithAnyAvailableResource={addTypeWithAnyAvailableResource}
          removeTypeWithAnyAvailableResource={
            removeTypeWithAnyAvailableResource
          }
        />
        <DXS.Scheduler
          className="scheduler-selected-resources"
          key={schedulerViewKey}
          ref={schedulerRef}
          firstDayOfWeek={firstDayOfWeekIndex}
          onContentReady={onSchedulerContentReady}
          editing={editingSettings}
          onAppointmentClick={cancelClickOperation}
          onAppointmentDblClick={cancelClickOperation}
          dataSource={appointmentsData}
          defaultCurrentView={'timelineWeek'}
          currentDate={startOfShownDatePeriod}
          height={'100%' as any}
          groups={groups}
          cellDuration={cellDurationInMinutes}
          dateCellRender={dateCellRender}
          appointmentComponent={appointmentComponent}
          showCurrentTimeIndicator={false}
        >
          <DXS.Resource
            fieldExpr={resourceId}
            allowMultiple={false}
            dataSource={resourcesForScheduler}
          />
        </DXS.Scheduler>
        {schedulerReady && (
          <PortalToTable
            key={selectionViewKey + startOfShownDatePeriod.toString()}
            schedulerRef={schedulerRef}
          >
            <IntervalSelection
              schedulerRef={schedulerRef}
              interval={selectedInterval}
              offsetDetector={offsetDetector}
              timeDetector={timeDetector}
              onIntervalChanged={onSelectedIntervalChanged}
              height={resourcesForScheduler.length * cellHeight || cellHeight}
              cellDurationInMills={
                schedulerParameters?.cellDurationInMilliseconds
              }
              cellWidth={schedulerParameters?.cellWidth}
              minDateTime={schedulerParameters?.start}
              maxDateTime={schedulerParameters?.end}
              takenTimeSlots={takenTimeSlotsWithUnavailable}
              stepsInCell={stepsInCell ? stepsInCell : isWeekView ? 24 : 2} //(in night view cell takes 24 hours, we can step by 1 hour; in day view cell is 1 hour, we can step by 30 minutes)
            />
          </PortalToTable>
        )}
      </div>
    );
  },
);
const editingSettings = {
  allowAdding: false,
  allowDeleting: false,
  allowResizing: true,
  allowDragging: false,
  allowEditingTimeZones: false,
  allowUpdating: true,
};

function cancelClickOperation(e: Cancelable) {
  e.cancel = true;
}
